// Asks Artemis Helper, in this same browser, whether it is alive. The Helper's
// presence-content.js (chrome_plugin/src/presence-content.ts) answers through
// window.postMessage. A Helper that has been killed or switched off cannot
// report that itself, but this page, open in the same browser, can.

const PING = "artemis-helper-ping";
const PONG = "artemis-helper-pong";
/** Posted by a content script whose Helper has just been killed or switched off. */
const GONE = "artemis-helper-gone";

export interface HelperGone {
	detected_at: string;
	/** When that content script started, i.e. when its Helper was known alive. */
	watching_since: string;
}

/** Calls `onGone` each time a content script in this page sees its Helper die;
 * returns the unsubscribe. */
export const onHelperGone = (onGone: (gone: HelperGone) => void): (() => void) => {
	const onMessage = (event: MessageEvent) => {
		if (event.source === window && event.data?.type === GONE) {
			onGone({
				detected_at: event.data.detected_at,
				watching_since: event.data.watching_since,
			});
		}
	};
	window.addEventListener("message", onMessage);
	return () => window.removeEventListener("message", onMessage);
};

/** How long the page waits for every copy of the content script to answer. */
const PROBE_TIMEOUT_MS = 1500;

/**
 * - alive: the Helper answered; its fields say what it is doing.
 * - unreachable: the content script is there but its Helper did not answer.
 * - context_invalidated: only a copy from a Helper run that has since ended
 *   (killed, switched off or reloaded) is left in this page.
 * - no_helper: nothing answered: no Helper in this browser, or it was not
 *   running when this page loaded.
 */
export type HelperPresenceStatus =
	| "alive"
	| "unreachable"
	| "context_invalidated"
	| "no_helper";

export interface HelperPresence {
	status: HelperPresenceStatus;
	worker_id?: string;
	version?: string;
	plugin_start_time?: string | null;
	last_poll_ok_at?: string | null;
	poll_executing?: boolean;
	error?: string;
}

/** A page can hold a live copy of the content script next to dead ones from
 * earlier Helper runs; the most alive answer wins. */
const STATUS_RANK: Record<HelperPresenceStatus, number> = {
	alive: 3,
	unreachable: 2,
	context_invalidated: 1,
	no_helper: 0,
};

export const probeHelper = (): Promise<HelperPresence> =>
	new Promise((resolve) => {
		const nonce = crypto.randomUUID();
		let best: HelperPresence = { status: "no_helper" };
		const onMessage = (event: MessageEvent) => {
			if (
				event.source !== window ||
				event.data?.type !== PONG ||
				event.data.nonce !== nonce
			) {
				return;
			}
			const answer: HelperPresence = { ...event.data };
			delete (answer as Partial<typeof event.data>).type;
			delete (answer as Partial<typeof event.data>).nonce;
			if (STATUS_RANK[answer.status] > STATUS_RANK[best.status]) {
				best = answer;
			}
		};
		window.addEventListener("message", onMessage);
		window.postMessage({ type: PING, nonce }, window.location.origin);
		window.setTimeout(() => {
			window.removeEventListener("message", onMessage);
			resolve(best);
		}, PROBE_TIMEOUT_MS);
	});
